import { Form, Alert } from 'antd';
import { Text, useToast } from '@quinyx/ui';
import { useForm } from 'react-hook-form';

import ShowConfig from './ShowConfig';
import {
  useDeleteClassicSSOConfig,
  useGetClassicSSOConfig,
} from '../../../hooks/support/classic-sso';
import StepInput from '../../../components/StepsComponent/StepInput';
import { withSupportLayout } from '../../../layouts/support';
import StepsComponent, {
  NextCallback,
} from '../../../components/StepsComponent';
import { useTranslate } from '../../../hooks/translate';

const ClassicSSO = () => {
  const { t } = useTranslate();
  const classicSSOForm = useForm<{ customerId: string }>();
  const createToast = useToast();
  const { getClassicSSO, ssoConfig } = useGetClassicSSOConfig();
  const { deleteClassicSSO, deletedConfig } = useDeleteClassicSSOConfig();

  const nextCallback: NextCallback = async (current, setStepStatusError) => {
    switch (current) {
      case 0:
        {
          const isClassicSSOFormValid = await classicSSOForm.trigger();
          if (!isClassicSSOFormValid) {
            return false;
          }

          const { customerId } = classicSSOForm.getValues();
          if (customerId) {
            try {
              await getClassicSSO(customerId);
            } catch (error) {
              setStepStatusError(t('support.classicSso.fetchFailed'));
            }
          }
        }
        break;

      case 1:
        {
          if (!ssoConfig) {
            createToast({
              message: t('toast.noConfigToDelete'),
              type: 'negative',
            });
            return false;
          }
          const { customerId } = classicSSOForm.getValues();
          if (customerId) {
            try {
              await deleteClassicSSO({
                customerId,
              });
              createToast({
                message: t('toast.classicSsoConfigDeleted'),
                type: 'positive',
              });
            } catch (error) {
              setStepStatusError(t('support.classicSso.deleteFailed'));
            }
          }
        }
        break;

      case 2: {
        classicSSOForm.reset();
        break;
      }
      default:
        break;
    }
    return true;
  };

  const steps = [
    {
      title: t('support.classicSso.getByCustomerId'),
      content: (
        <Form layout="vertical">
          <Form.Item label={t('support.classicSso.customerId')}>
            <StepInput
              error={!!classicSSOForm.formState.errors.customerId}
              required
              key="customerId"
              name="customerId"
              control={classicSSOForm.control}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      title: t('support.classicSso.deleteSsoConfig'),
      content: (
        <>
          <ShowConfig data={ssoConfig} />
          {ssoConfig && 'id' in ssoConfig ? (
            <Alert
              style={{ maxWidth: '500px', margin: '40px auto 0 auto' }}
              message={t('common.pleaseNote')}
              description={t('support.classicSso.deleteWarning')}
              type="info"
              showIcon
            />
          ) : null}
        </>
      ),
      nextLabel: t('common.delete'),
    },
    {
      title: t('support.classicSso.verifyDeletion'),
      content: <ShowConfig data={deletedConfig} />,
    },
  ];

  return (
    <>
      <Text color="SurfaceOnSurfaceAlt">
        {t('support.classicSso.pageDescription')}
      </Text>
      <StepsComponent nextCallback={nextCallback} steps={steps} />
    </>
  );
};
export default withSupportLayout(ClassicSSO);
